import React, { useState, useEffect, useCallback } from "react";
import moment from "moment";
import { Table, Tooltip } from "antd";
import ChantPopConfirm from "../../../../../CommonComponents/ChantTooltip/ChantPopConfirm";
import Ticket from "../../../../../models/admin/Ticket";
import {
  fbGetTicketDetails,
  fetchAllGroupTickets,
} from "../../../../../services/firebaseService/endPoints/admin/tickets";
import {
  GUIDE_ENGAGE,
  TOPIC_SELL_TICKETS,
} from "../../../../../constants/gettingStarted";
import { isMobile } from "../../../../../utils/helper";
import {
  DeleteOutlined,
  EyeOutlined,
  PlusOutlined,
  ReloadOutlined,
} from "@ant-design/icons";
import dayjs from "dayjs";
import ScreenHeader from "../../../../../CommonComponents/ScreenHeader";
import TabNav from "../../../../../CommonComponents/TabNav/TabNav";
import { useNavigate, useParams } from "react-router-dom";
import useAdminDataStore, { selectCurrentGroupData } from "../../../../../store/adminData/adminData";
import { ChantButton } from "../../../../../CommonComponents";
import TicketsExpandableRow from "./TicketsExpandableRow";
import { toast } from "react-toastify";
import logger from "../../../../../utils/logger";
import useCurrency from "../../../../../utils/hooks/useCurrency";
import {
  TICKET_TABLE_WIDTHS,
  getResponsiveWidth,
} from "../../../../../constants/tableWidths";
import { chantApi } from "../../../../../utils/chantApi";

const PAGE_SIZE = 20;

const TicketsTable = () => {
  const navigate = useNavigate();
  const params = useParams();

  const groupCurrency = useCurrency();

  const groupData = useAdminDataStore(selectCurrentGroupData);

  const [tab, setTab] = useState("current");
  const [isLoading, setIsLoading] = useState(false);
  const [tableData, setTableData] = useState([]);
  const [totalCount, setTotalCount] = useState(null);
  const [updatePublishStatusLoading, setUpdatePublishStatusLoading] =
    useState(false);
  const [pagination, setPagination] = useState({
    current: 1,
    pageSize: PAGE_SIZE,
    total: 0,
    showSizeChanger: true,
    pageSizeOptions: ["10", "20", "50", "100"],
  });

  // Track if we're fetching for past tab (needs pagination)
  const [isPastTab, setIsPastTab] = useState(false);

  // Track current sort order
  const [sortOrder, setSortOrder] = useState(null);

  const handleEditTicket = (data) => {
    navigate(`/admin/tickets/edit/${groupData?.id}/${data.id}`);
  };

  const handleViewTicket = (data) => {
    navigate(`/admin/tickets/${groupData?.id}/${data.id}`);
  };

  const handleTicketRefresh = () => {
    fetchTickets(tab);
  };

  const updatePublishStatus = async (id, status) => {
    try {
      setUpdatePublishStatusLoading(true);
      let doc = await fbGetTicketDetails(id, groupData?.id);

      let data = doc.data.body.data;

      let ticketData = new Ticket();
      ticketData.fromDataSet(data);

      let updateData = new Ticket();
      updateData.toDataSet(ticketData, status);
      let obj = {
        groupTicket: updateData.groupTicket,
        match: updateData.match,
        ticketId: id,
        groupId: groupData?.id,
        oldTicketsCount: ticketData.oldTicketsCount,
      };

      await chantApi.post(
        `${import.meta.env.VITE_GCP_BASE_URL}/tickets/editSingleGroupTicket`,
        obj,
      );
      toast.success("Updated ticket successfully");
      await fetchTickets(tab);
    } catch (error) {
      toast.error("Error updating publish status");
      logger.error(error);
    } finally {
      setUpdatePublishStatusLoading(false);
    }
  };

  const fetchTickets = useCallback(
    async (selectedTab, paginationOptions = {}) => {
      try {
        setIsLoading(true);

        let fetchOptions = {};

        if (selectedTab === "current") {
          fetchOptions = {
            eventType: selectedTab,
            orderDirection: "desc",
          };
        } else if (selectedTab === "past") {
          fetchOptions = {
            eventType: selectedTab,
            orderDirection: "desc",
            ...paginationOptions,
          };
        }

        const response = await fetchAllGroupTickets(
          groupData?.id,
          fetchOptions,
        );

        setTotalCount(response?.pagination?.total || null);

        let ticketsData = [];

        if (response?.body?.length > 0) {
          useAdminDataStore.getState().setGettingStartedItemStatus(
            groupData?.id,
            GUIDE_ENGAGE,
            TOPIC_SELL_TICKETS,
            true,
          );
        }

        // Process tickets - filtering is now done in the API
        const processedTickets = response.body.map((ticket, index) => {
          let newData = new Ticket();
          newData.fromAPIData(ticket);
          let link = `https://group.chant.fan/${groupData?.groupName.toLowerCase().replaceAll(" ", "")}/${newData.matchId}/tickets/${newData.id}`;
          return {
            ...newData,
            pageId: index,
            link: link,
            key: selectedTab === "past" ? `${index}` : undefined,
          };
        });
        ticketsData = processedTickets;

        setTableData(ticketsData || []);

        if (response.pagination) {
          const newPaginationState = {
            total: response.pagination.total || 0,
            current:
              Math.floor(
                (response.pagination.offset || 0) /
                  (response.pagination.limit || PAGE_SIZE),
              ) + 1,
            pageSize: response.pagination.limit || PAGE_SIZE,
          };
          setPagination((prev) => ({
            ...prev,
            ...newPaginationState,
          }));
        } else {
          setPagination((prev) => ({
            ...prev,
            total: ticketsData.length,
            current: 1,
          }));
        }
      } catch (error) {
        setTableData([]);
        toast.error("Error fetching tickets");
        logger.error(error);
      } finally {
        setIsLoading(false);
      }
    },
    [groupData?.id, groupData?.groupName],
  );

  useEffect(() => {
    if (groupData?.id) {
      fetchTickets(tab);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [groupData?.id]);

  const columns = [
    {
      align: "center",
      title: "Action",
      width: TICKET_TABLE_WIDTHS.ACTION,
      className: "header-white",
      render: (data) => {
        if (data.loading) {
          return {
            children: <></>,
            colSpan: 3,
          };
        }
        return (
          <div className="flex-center" style={{ gap: "12px" }}>
            {tab === "current" && (
              <Tooltip title="edit">
                <i
                  className="fa fa-pencil cursor-pointer"
                  aria-hidden="true"
                  onClick={() => {
                    handleEditTicket(data);
                  }}
                />
              </Tooltip>
            )}

            <Tooltip title="view">
              <EyeOutlined
                onClick={() => {
                  handleViewTicket(data);
                }}
                style={{ fontSize: 18 }}
              />
            </Tooltip>
            {data.groupTicket.publishStatus === "live" && (
              <ChantPopConfirm
                disabled={updatePublishStatusLoading}
                title="Are you sure? Tickets will no longer appear."
                onCancel={() => {}}
                onConfirm={() => updatePublishStatus(data.id, "archived")}
              >
                <DeleteOutlined
                  style={{ fontSize: 18 }}
                  disabled={updatePublishStatusLoading}
                />
              </ChantPopConfirm>
            )}
            {data.groupTicket.publishStatus === "saved" && (
              <ChantPopConfirm
                title="Are you sure? Notification will be sent."
                onCancel={() => {}}
                onConfirm={() => {
                  updatePublishStatus(data.id, "live");
                }}
              >
                <ChantButton>Post Live</ChantButton>
              </ChantPopConfirm>
            )}
          </div>
        );
      },
    },
    {
      align: "center",
      title: "Status",
      width: TICKET_TABLE_WIDTHS.STATUS,
      className: "header-white",
      key: "publishStatus",
      dataIndex: "groupTicket",
      render: (data) => {
        const status = data.publishStatus;

        if (status === "live") {
          return <span>Live</span>;
        }
        if (status === "saved") {
          return <span>Saved</span>;
        }
        if (status === "archived") {
          return <span>Archived</span>;
        }
        if (status === "sale-ended") {
          return <span>Sale Ended</span>;
        }
        return <span></span>;
      },
    },
    {
      align: "center",
      title: "Web",
      width: TICKET_TABLE_WIDTHS.WEB,
      className: "header-white",
      key: "link",
      dataIndex: "link",
      render: (data, record) =>
        record.groupTicket.showOnWeb ? (
          <a href={data} target="_blank" rel="noopener noreferrer">
            Link
          </a>
        ) : (
          ""
        ),
    },
    {
      align: "center",
      title: "Match",
      width: getResponsiveWidth(TICKET_TABLE_WIDTHS.MATCH),
      key: "matchName",
      className: "header-white",
      dataIndex: "matchName",
    },

    {
      align: "center",
      title: "Type",
      width: getResponsiveWidth(TICKET_TABLE_WIDTHS.TYPE),
      key: "groupTicket.ticketType",
      dataIndex: "groupTicket",
      className: "header-white",
      render: (data) => {
        return data.ticketType;
      },
    },
    {
      title: window.screen.width > 500 ? "Event Name" : "Name",
      width: TICKET_TABLE_WIDTHS.EVENT_NAME,
      key: "groupTicket.eventName",
      className: "header-white",
      align: "center",
      render: (record) =>
        record.groupTicket.ticketType === "Group Match"
          ? record.groupTicket.ticketDetail.section
          : record.groupTicket.eventName,
    },
    {
      title: "Revenue",
      width: getResponsiveWidth(TICKET_TABLE_WIDTHS.REVENUE),
      key: "revenue",
      dataIndex: "groupTicket",
      className: "header-white",
      align: "center",
      render: (data) => {
        let dataAsFloat = parseFloat((data?.collectedAmount || "0").toString());
        let dataAsString = dataAsFloat.toFixed(
          Math.round(dataAsFloat) === dataAsFloat ? 0 : 2,
        );
        return (
          <span>
            {data?.collectedAmount ? `${groupCurrency}${dataAsString}` : "-"}
          </span>
        );
      },
    },
    {
      title: (
        <div
          style={{ textAlign: "center" }}
          className="main-tickets-table-quantity-column"
        >
          <div className="main-tickets-table-quantity-column-content">
            <p style={{ fontSize: "14px" }}>Quantity</p>
            <p style={{ fontSize: "12px", color: "#666" }}>Total/Sold/Avail</p>
          </div>
        </div>
      ),
      width: getResponsiveWidth(TICKET_TABLE_WIDTHS.QUANTITY),
      key: "quantity",
      dataIndex: "groupTicket.quantity",
      align: "center",
      className: "header-white",
      render: (data, record) => {
        let totalQuantity = 0;
        let totalSoldTickets = 0;

        if (record && record?.groupTicket?.tickets?.length > 0) {
          record.groupTicket.tickets.forEach((ticket) => {
            totalQuantity = totalQuantity + parseInt(ticket.quantity);
            totalSoldTickets = totalSoldTickets + ticket.soldTickets;
          });
        }

        return `${totalQuantity} / ${totalSoldTickets} / ${
          totalQuantity - totalSoldTickets
        }`;
      },
    },
    {
      title: "Checkins",
      width: getResponsiveWidth(TICKET_TABLE_WIDTHS.CHECKINS),
      key: "checkins",
      dataIndex: "checkins",
      align: "center",
      className: "header-white",
      render: (data, record) => {
        return record?.groupTicket?.tickets?.reduce((acc, curr) => {
          return (acc += curr.noOfCheckins);
        }, 0);
      },
    },

    {
      title: (
        <div style={{ textAlign: "center" }}>
          <p style={{ fontSize: "14px" }}>Price</p>
          <p style={{ fontSize: "12px", color: "#666" }}>Member/Non-Member</p>
        </div>
      ),
      width: TICKET_TABLE_WIDTHS.PRICE,
      align: "center",
      className: "header-white",
      key: "price",
    },
    {
      align: "center",
      title: window.screen.width > 500 ? "Limit / Person?" : "Limit",
      key: "ticketLimitPerMember",
      className: "header-white",
    },
    {
      align: "center",
      title: window.screen.width > 500 ? "Event Date" : "Date",
      width: TICKET_TABLE_WIDTHS.EVENT_DATE,
      key: "eventDate",
      className: "header-white",
      dataIndex: "groupTicket",
      sorter: true,
      sortDirections: ["ascend", "descend"],
      sortOrder: sortOrder,
      render: (groupTicket) => {
        let startDate = "";
        if (groupTicket.eventDate) {
          startDate = moment.unix(groupTicket.eventDate);
          startDate = startDate.isValid()
            ? startDate.format("Do MMM, YYYY")
            : "";
        }
        return <span>{startDate}</span>;
      },
    },
    {
      title: "Sale End Date",
      width: getResponsiveWidth(TICKET_TABLE_WIDTHS.SALE_END_DATE),
      key: "saleEndDate",
      className: "header-white",
      align: "center",
      dataIndex: "groupTicket",
      render: (data) => {
        return dayjs.unix(data.saleEndDate).format("Do MMM, YYYY");
      },
    },
    {
      align: "center",
      title: window.screen.width > 500 ? "Event Code" : "Code",
      width: TICKET_TABLE_WIDTHS.EVENT_CODE,
      key: "eventCode",
      className: "header-white",
      dataIndex: "groupTicket",
      render: (data) => {
        return data.eventCode;
      },
      sortDirections: ["ascend", "descend"],
    },
  ];

  const getCurrentTab = () => {
    if (params.tab === "update") {
      return "update";
    }
    return tab;
  };

  const menuItems = [];
  if (params.tab === "all") {
    menuItems.push(
      {
        key: "current",
        label: "Current",
      },
      {
        key: "past",
        label: "Past",
      },
    );
  }
  if (params.tab === "update") {
    menuItems.push({
      key: "update",
      label: "Update",
    });
  }

  const handleTabChange = (tabKey) => {
    setTab(tabKey);
    setIsPastTab(tabKey === "past");

    setPagination((prev) => ({
      ...prev,
      current: 1,
      total: 0,
    }));
    setSortOrder(null);

    if (tabKey === "current") {
      fetchTickets("current");
    } else if (tabKey === "past") {
      fetchTickets("past", { limit: PAGE_SIZE, offset: 0 });
    }
  };

  return (
    <div className="tickets-container" style={{ padding: "12px" }}>
      <ScreenHeader
        title="Tickets"
        elementCount={totalCount}
        actions={
          params.tab === "all" && (
            <div style={{ display: "flex", gap: "12px" }}>
              <ChantButton
                onClick={() => {
                  navigate("/admin/tickets/create");
                }}
                type="primary"
              >
                {isMobile() ? (
                  <PlusOutlined style={{ fontSize: "20px" }} />
                ) : (
                  "Create Group Ticket"
                )}
              </ChantButton>
              <ChantPopConfirm
                title="Refresh tickets?"
                onConfirm={() => {
                  handleTicketRefresh();
                }}
                onCancel={() => {}}
                okText="Yes"
                cancelText="No"
              >
                <ChantButton type="secondary">
                  {isMobile() ? (
                    <ReloadOutlined style={{ fontSize: "20px" }} />
                  ) : (
                    "Refresh"
                  )}
                </ChantButton>
              </ChantPopConfirm>
            </div>
          )
        }
      />

      <div style={{ marginTop: "2px" }}>
        <TabNav
          items={menuItems}
          activeKey={getCurrentTab()}
          handleTabChange={handleTabChange}
        />
      </div>

      <div className="tickets-table">
        <Table
          bordered
          dataSource={tableData}
          columns={columns}
          loading={isLoading}
          expandIconAsCell={true}
          expandable={{
            expandedRowRender: (record) =>
              TicketsExpandableRow(record, groupCurrency),
          }}
          bodyStyle={{
            fontSize: window.screen.width > 500 ? "18px" : "12px",
            backgroundColor: "var(--primary-dashboard-background)",
          }}
          onChange={(paginationInfo, filters, sorter, extra) => {
            if (sorter && sorter.order) {
              setSortOrder(sorter.order);
            }

            if (isPastTab) {
              const offset =
                (paginationInfo.current - 1) * paginationInfo.pageSize;

              const orderDirection = sorter.order === "ascend" ? "asc" : "desc";

              fetchTickets("past", {
                limit: paginationInfo.pageSize,
                offset: extra.action === "sort" ? 0 : offset,
                orderDirection: orderDirection,
              });
            }
          }}
          pagination={
            pagination.total > pagination.pageSize
              ? {
                  ...pagination,
                  position: ["bottomLeft"],
                  showTotal: (total, range) => {
                    return `${range[0]}-${range[1]} of ${total} items`;
                  },
                  style: {
                    backgroundColor: "var(--primary-dashboard-background)",
                    padding: "8px 12px",
                    marginTop: "2px",
                    width: "calc(100% - 1px)",
                    marginLeft: "1px",
                  },
                }
              : false
          }
          rowKey="id"
          key="id"
          scroll={{ x: "max-content" }}
        />
      </div>
    </div>
  );
};

export default TicketsTable;
